import { Box, styled } from '@mui/material';
import classnames from 'classnames';
import dayjs from 'dayjs';

import { ENOPOrganType, EResourceType, HUB_NAME_MAPPING } from '#shared';

import { useHubDetails } from '../../data-context';

import { TableCell } from './TableCell';

import type { DeviceResource, HubReportStatus, HumanResource } from '#entities';
import type { GroupedDrawerData } from '../../ResourceDrawer';

// Synced with BE structure
enum organIndexMap {
  Liver,
  Lungs,
  Heart,
}

// Synced with BE structure
enum teamIndexMap {
  Device,
  Surgeon,
  Specialist,
}

export interface TableRowItemProps {
  row: HubReportStatus;
  isEven: boolean;
}

const PREFIX = 'TSMED-HubsReport-TableRowItem';

const tableRowItemClasses = {
  row: `${PREFIX}-row`,
  even: `${PREFIX}-even`,
  odd: `${PREFIX}-odd`,
  hubCell: `${PREFIX}-hubCell`,
  city: `${PREFIX}-city`,
  abbreviation: `${PREFIX}-abbreviation`,
  dayGroups: `${PREFIX}-dayGroups`,
  dayGroup: `${PREFIX}-dayGroup`,
  today: `${PREFIX}-today`,
  cell: `${PREFIX}-cell`,
  amount: `${PREFIX}-amount`,
  time: `${PREFIX}-time`,
};

const Root = styled('li')(({ theme: { utils } }) => ({
  display: 'grid',
  // Grid: hub cell + 3 organs × 2 days each = 1 + 6 columns
  gridTemplateColumns: 'var(--hub-column-width) repeat(3, 1fr 1fr)',
  [`&.${tableRowItemClasses.even}`]: {
    background: '#141f29',
    [`.${tableRowItemClasses.hubCell}`]: {
      background: '#0e151b',
    },
  },
  [`&.${tableRowItemClasses.odd}`]: {
    background: '#1f2b37',
    [`.${tableRowItemClasses.hubCell}`]: {
      background: '#141f29',
    },
  },
  [`.${tableRowItemClasses.hubCell}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 8px',
    justifyContent: 'center',
    position: 'sticky',
    left: 35,
    zIndex: 1,
    background: 'inherit',
    borderRight: '2px solid #000',
    [`.${tableRowItemClasses.city}`]: {
      fontSize: utils.clamp(12, 20),
      color: '#b6b6b6',
    },
    [`.${tableRowItemClasses.abbreviation}`]: {
      fontSize: utils.clamp(10, 12),
      color: '#87888b',
    },
  },
  [`.${tableRowItemClasses.dayGroups}`]: {
    display: 'contents', // This flattens the wrapper, making day-groups direct children of the grid
  },
  [`.${tableRowItemClasses.dayGroup}`]: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    position: 'relative',
    borderLeft: '2px solid #000',
    [`&:first-of-type`]: {
      border: 'none',
    },
  },
}));

export const TableRowItem = ({ row, isEven }: TableRowItemProps) => {
  const { timezone, city, state } = row.location;
  const { resources = [] } = useHubDetails(row.id);
  const timeZoneAbbreviation = dayjs().tz(timezone).format('z');

  const groupedData: GroupedDrawerData = {
    Device: {},
    Surgeon: {},
    Specialist: {},
  };

  resources.forEach(({ details, teamMemberType: resourceType }) => {
    details.forEach(({ organType, resources }) => {
      if (resourceType === EResourceType.Device) {
        // Device branch
        const bucket = (groupedData[EResourceType.Device][organType] ??= []);

        bucket.push(...(resources as DeviceResource[]));
      } else {
        // Surgeon / Specialist branch (humans)
        const bucket = (groupedData[resourceType][organType] ??= []);

        bucket.push(...(resources as HumanResource[]));
      }
    });
  });

  return (
    <Root
      className={classnames(tableRowItemClasses.row, {
        [tableRowItemClasses.even]: isEven,
        [tableRowItemClasses.odd]: !isEven,
      })}
    >
      <Box className={tableRowItemClasses.hubCell}>
        <Box className={tableRowItemClasses.city}>
          {HUB_NAME_MAPPING[
            `${state} ${city}`.replaceAll(/\W/g, '_').toUpperCase()
          ] ?? [state, city].filter(Boolean).join(' ')}
        </Box>
        <Box className={tableRowItemClasses.abbreviation}>
          {timeZoneAbbreviation}
        </Box>
      </Box>
      <Box className={tableRowItemClasses.dayGroups}>
        {[ENOPOrganType.Liver, ENOPOrganType.Heart, ENOPOrganType.Lungs]
          .map(organ =>
            (['today', 'tomorrow'] as ['today', 'tomorrow']).map(day => (
              <Box
                key={`${day}-${organ}`}
                className={classnames(
                  tableRowItemClasses.dayGroup,
                  tableRowItemClasses.today,
                )}
              >
                <TableCell
                  details={
                    row.staffInfo[teamIndexMap.Specialist].details[
                      organIndexMap[organ]
                    ]
                  }
                  location={row.location}
                  organ={organ}
                  day={day}
                  resourceType={EResourceType.Specialist}
                  groupedDrawerData={groupedData}
                />
                <TableCell
                  details={
                    row.staffInfo[teamIndexMap.Surgeon].details[
                      organIndexMap[organ]
                    ]
                  }
                  day={day}
                  location={row.location}
                  organ={organ}
                  resourceType={EResourceType.Surgeon}
                  groupedDrawerData={groupedData}
                />
                <TableCell
                  details={
                    row.staffInfo[teamIndexMap.Device].details[
                      organIndexMap[organ]
                    ]
                  }
                  day={day}
                  location={row.location}
                  organ={organ}
                  resourceType={EResourceType.Device}
                  groupedDrawerData={groupedData}
                />
              </Box>
            )),
          )
          .flat()}
      </Box>
    </Root>
  );
};
